import React from "react";
import { styled } from "@mui/system";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import BedOutlinedIcon from '@mui/icons-material/BedOutlined';
import { ObjectId } from "mongodb";

const Card = styled(Paper)({
    minWidth: '20rem',
    minHeight: '13rem',
    padding: '8px',
    gap: '5px',
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
	margin:'1rem 1rem 0 1rem',
});

const Details = styled("div")({
    minWidth: '18rem',
    minHeight: '74px',
    gap: "8px",
    display: 'flex',
    flexDirection: 'column',
});

const PropertyName = styled(Typography)({ 
    letterSpacing: '0.1px',
});

const LocationIcon = styled(LocationOnOutlinedIcon)({
    width: '16px',
    height: '16px',
    color: '#B05911',
});

const BedIcon = styled(BedOutlinedIcon)({
    width: '16px',
    height: '16px',
    color: '#B05911',
});

const InfoRow = styled("div")({
  display: "flex",
  flexDirection: "row",
  gap: "4px",
  alignItems: "center",
});

const Info = styled(Typography)({
    fontWeight: '400',
    fontSize: '12px',
    lineHeight: '166%',
    letterSpacing: '0.4px',
    color: '#666666',
});

const Image = styled("img")(({ theme }) => ({
    minHeight: '114px',
    backgroundColor: '#D9D9D9',
    borderRadius: '8px',
	display: "block",
	objectFit: "cover",
	/* XS breakpoint - 280.05 */
	[theme.breakpoints.up("xs")]: {
		height: "8rem",
	},
	/* iphone 5 - 320 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 39)]: {
		height: "5rem",
	},
	/* galaxy S8+ - 360 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 78)]: {
		height: "5.5rem",
	},
	/* iphone 12 pro and pixel - 390 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 108.95)]: {
		height: "5.75rem",
	},
	/* iphone XR - 414 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 130)]: {
		height: "6rem",
	},
	/* iphone 14 pro max - 430 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 149)]: {
		height: "6.25rem",
	},
	/* 457 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 176)]: {
		height: "6.75rem",
	},
	/* 485 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 204)]: {
		height: "7.25rem",
	},
	/* 512 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 231)]: {
		height: "7.5rem",
	},
	/* 540 */
	[theme.breakpoints.up("xsPlus")]: {
		height: "7.75rem",
	},
	/* 570  ~~3 cards start~~ */
	[theme.breakpoints.up(theme.breakpoints.values.xsPlus + 30)]: {
		height: "5.5rem",
	},
	/* 600  ~~2 cards start~~ */
	[theme.breakpoints.up("sm")]: {
		height: "7.75rem",
	},
	/* 642 ~~3 cards start~~ */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 42)]: {
		height: "6rem",
	},
	/* 684 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 84)]: {
		height: "6.25rem",
	},
	/* ipad Mini - 768 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 167)]: {
		height: "7rem",
	},
	/* ipad Air - 820 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 219)]: {
		height: "8rem",
	},
	/* 860 */
	[theme.breakpoints.up("sm860")]: {
		height: "8.25rem",
	},
	/* 910 */
	[theme.breakpoints.up("sm910")]: {
		height: "8.5rem",
	},
	/* 960 */
	[theme.breakpoints.up("md")]: {
		height: "8.75rem",
	},
	/* iPadPro - 1024 */
	[theme.breakpoints.up(theme.breakpoints.values.md + 64)]: {
		height: "9.25rem",
	},
	/* 1107 */
	[theme.breakpoints.up(theme.breakpoints.values.md + 147)]: {
		height: "9.5rem",
	},
	/* 1190 ~~Desktop Layout Start~~ */
	[theme.breakpoints.up("md1190")]: {
		height: "8rem",
	},
	/* 720p breakpoint */
	[theme.breakpoints.up("lg")]: {
		height: "8.5rem",
	},
	/* 1080p 125% breakpoint */
	[theme.breakpoints.up(theme.breakpoints.values.lg + 150)]: {
		height: "9rem",
	},
	/* 4k breakpoint 3840p */
	[theme.breakpoints.up(theme.breakpoints.values.xl + 1920)]: {
		height: "9rem",
	},
}));

const fallbackImage: string = "/images/uploads/property.jpg";
export default function SummaryPropertyCard({PropertyId}:{ PropertyId: string | ObjectId }) {
    const [propertyData, setPropertyData] = React.useState<any>({});

    React.useEffect(() => {
        if (PropertyId) {
            /* Fetching property data */
            fetch(`${process.env.PRODUCTION_API_URL}properties/cards/${PropertyId}?resize=""`, {
                method: "POST",
                headers: {
                    "Content-Type": "text/plain",
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    setPropertyData(data.property_data);
                });
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps*/
    }, [PropertyId]);
        
    const images = propertyData.images || [];

    return (
    <Card>
        <Image
            src={images && images !== undefined && images !== "" ? images : fallbackImage}
            alt="Property Image"
            referrerPolicy="no-referrer"
            loading="eager"
            width="100%"
        />
        <Details>
        <PropertyName variant='subtitle2'>{propertyData.title}</PropertyName>
        <InfoRow>
            <LocationIcon/>
            <Info>{propertyData.location?.title || 'N/A'}</Info>
        </InfoRow>
        <InfoRow>
            <BedIcon/>
            <Info>{propertyData.bhk?.min} - {propertyData.bhk?.max}</Info>
        </InfoRow>
        </Details>
    </Card>
    );
}
